import './kitStyles';
import React, { useCallback, useContext, useSyncExternalStore } from 'react';
import { ReactReduxContext } from 'react-redux';
import type { UserTheme } from '@/types';
import { useCabinetRootAttributes } from '@/theme/cabinetTokens';
import { Icon } from './Icon';
import type { IconName } from './icons';

/**
 * WEB-4003 юнит 1 — общее пустое состояние кабинета (эталон спек-репо
 * f73761d0, `design/cabinet-rescin/kit.jsx:476, 1321–1328`; образец разметки —
 * `pages/SmartLinks.tsx`): значок раздела первым ребёнком `.empty`, затем
 * заголовок `.em-t`, пояснение `.em-d` и действие (дети). Картинка сервиса —
 * плитка значка и веер двух «экранов воронки» — рисуется правилом
 * `:is(.empty:not([style]), .an-empty) > svg.ico:first-child` в
 * `styles/cabinet-kit-1b.css`, поэтому инлайн-стиля на `.empty` не бывает
 * никогда: отступ «без своего» — класс `flush`.
 *
 * Свой корень `.ds.em-host` (без своего короба, `display: contents`) с темой
 * человека, как у `PageLoading`: многие таблицы antd стоят на страницах вне
 * `.ds`, а переменные темы живут только под `.ds`. Тема берётся из store мягко:
 * без `Provider` (тесты, окна вне дерева store) — общим правилом
 * `useResolvedTheme` (сохранённый выбор, тема устройства).
 */
export interface EmptyProps {
  /** Значок раздела; по умолчанию `stack`. */
  icon?: IconName;
  title?: React.ReactNode;
  /** Пояснение второй строкой. */
  text?: React.ReactNode;
  /** Без своего отступа — когда отступ даёт карточка. */
  flush?: boolean;
  /** Действие (кнопка) под текстом. */
  children?: React.ReactNode;
  'data-testid'?: string;
}

type ThemeState =
  { auth?: { user?: { theme?: UserTheme } | null } } | undefined;

const NO_STORE = () => () => undefined;

function useThemeSoft(): UserTheme | undefined {
  const store = useContext(ReactReduxContext)?.store;
  const subscribe = useCallback(
    (cb: () => void) => (store ? store.subscribe(cb) : NO_STORE()),
    [store],
  );
  const read = () => (store?.getState() as ThemeState)?.auth?.user?.theme;
  return useSyncExternalStore(subscribe, read, read);
}

export function Empty({
  icon = 'stack',
  title,
  text,
  flush,
  children,
  'data-testid': testId = 'empty',
}: EmptyProps): React.ReactElement {
  const root = useCabinetRootAttributes(useThemeSoft());
  return (
    <div {...root} className={`${root.className} em-host`}>
      <div className={flush ? 'empty flush' : 'empty'} data-testid={testId}>
        <Icon name={icon} size={28} />
        {title != null && title !== false ? (
          <div className="em-t">{title}</div>
        ) : null}
        {text != null && text !== false ? (
          <div className="em-d">{text}</div>
        ) : null}
        {children}
      </div>
    </div>
  );
}
